import React from 'react';
import { FilterType } from 'utilities/enum-utils';
import { timeZone } from 'interfaces/timezone-type';
import {
  FilterMenuDateRange,
  FilterMenuSearchLookup,
  FilterMenuCheckboxList,
  FilterMenuRadioList,
} from 'common-components/filter';
import { IFilter, IFilterDateRange } from 'interfaces/filter-interfaces';
import { useFetchTagsLanguages } from 'stores/hooks/query-hooks/use-query-fetch-tags';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import { IGetTagsResponse } from 'stores/queries/tags/tag-queries';
import { SaveNewFilterValueDateRange } from './FilterMenuDateRange';
import { DateRangeFilterV2 } from './date-range-filter-v2/date-range-filter-v2';
import moment from 'moment';

type SaveNewFilterValue = (filterType: string, newFilterValue: string, selectionLabel: string) => void;

type IFilterItemMenuBaseProps = {
  canRemove?: boolean;
  removeFilter: (filterType: string) => void;
  optionsQuery?: IGetTagsResponse;
  optionsIsLoading?: boolean;
  onClose: () => void;
  dateFilterV2?: boolean;
};

type IFilterItemMenuDefaultProps = {
  filter: IFilter;
  saveNewFilterValue: SaveNewFilterValue;
  displayTimezone?: never;
  onClose: () => void;
};

type IFilterItemMenuDateRangeProps = {
  filter: IFilterDateRange;
  saveNewFilterValue: SaveNewFilterValueDateRange;
  displayTimezone: timeZone;
  onClose?: () => void;
};

export type IFilterItemMenuProps = IFilterItemMenuBaseProps &
  (IFilterItemMenuDefaultProps | IFilterItemMenuDateRangeProps);

const isDateRangeFilter = (filter: IFilter | IFilterDateRange): filter is IFilterDateRange => {
  return (
    filter.filter === FilterType.DATE_RANGE ||
    filter.filter === FilterType.INVOICE_DATE ||
    filter.filter === FilterType.DATE_PAID ||
    filter.filter === FilterType.ACTIVE_WORKFLOWS_DATE ||
    filter.filter === FilterType.DATE_ADDED
  );
};

const FilterItemMenu = ({
  filter,
  canRemove,
  saveNewFilterValue,
  removeFilter,
  optionsQuery,
  optionsIsLoading,
  onClose,
}: IFilterItemMenuBaseProps & IFilterItemMenuDefaultProps) => {
  const filterType = filter.filter;

  /* If a filterType dependant on a useQuery (see functional wrapper switch) is defined 
    optionsData needs to be available (not loading) before rendering */
  if (optionsIsLoading) {
    return <SpinningLoader size={25} message={'Loading filter data'} />;
  }

  if (
    filterType === FilterType.CUSTOMER ||
    filterType === FilterType.WORKER ||
    filterType === FilterType.BATCH_AUTHOR ||
    filterType === FilterType.PREFERRED_SUPPORT_WORKER ||
    filterType === FilterType.BLOCKED_SUPPORT_WORKER ||
    filterType === FilterType.SERVICE_DATE_TIMES ||
    filterType === FilterType.GROUP_SERVICE_SCHEDULES ||
    filterType === FilterType.LOCATION_BY_SUBURBS ||
    filterType === FilterType.SUPPORT_COORDINATOR ||
    filterType === FilterType.CASE_MANAGER ||
    filterType === FilterType.DEBTOR
  ) {
    return (
      <FilterMenuSearchLookup
        filter={filter}
        canRemove={canRemove}
        saveNewFilterValue={saveNewFilterValue}
        removeFilter={removeFilter}
        onClose={onClose}
      />
    );
  }

  if (
    filterType === FilterType.BOOKING_BILLING ||
    filterType === FilterType.PAID_BILLING ||
    filterType === FilterType.BATCH_BILLING
  ) {
    return (
      <FilterMenuRadioList
        filter={filter}
        saveNewFilterValue={saveNewFilterValue}
        removeFilter={removeFilter}
        canRemove={canRemove}
        onClose={onClose}
      />
    );
  }
  return (
    <FilterMenuCheckboxList
      filter={filter}
      saveNewFilterValue={saveNewFilterValue}
      removeFilter={removeFilter}
      canRemove={canRemove}
      optionsQuery={optionsQuery}
      onClose={onClose}
    />
  );
};

const FilterItemMenuWithQueries = ({
  canRemove,
  displayTimezone,
  filter,
  removeFilter,
  saveNewFilterValue,
  onClose,
  dateFilterV2,
}: IFilterItemMenuProps) => {
  const { data: queryData, isLoading, error: queryError } = useFetchTagsLanguages();

  if (isDateRangeFilter(filter)) {
    if (dateFilterV2) {
      return (
        <DateRangeFilterV2
          value={{ startDate: filter.values[0]?.toDate?.() ?? null, endDate: filter.values[1]?.toDate?.() ?? null }}
          onChange={(newValue, label) => {
            const startDate = newValue.startDate ? moment(newValue.startDate) : null;
            const endDate = newValue.endDate ? moment(newValue.endDate) : null;
            saveNewFilterValue(filter.filter, [startDate, endDate], label);
          }}
          onClose={onClose}
        />
      );
    }
    return (
      <FilterMenuDateRange
        filter={filter}
        canRemove={canRemove}
        displayTimezone={displayTimezone as timeZone}
        saveNewFilterValue={saveNewFilterValue as SaveNewFilterValueDateRange}
        removeFilter={removeFilter}
        onClose={onClose}
      />
    );
  }

  switch (filter.filter) {
    case FilterType.LANGUAGES:
      return (
        <FilterItemMenu
          filter={filter}
          canRemove={canRemove}
          saveNewFilterValue={saveNewFilterValue as SaveNewFilterValue}
          removeFilter={removeFilter}
          optionsQuery={queryData}
          optionsIsLoading={isLoading || !!queryError}
          onClose={onClose}
        />
      );
    default:
      return (
        <FilterItemMenu
          filter={filter}
          canRemove={canRemove}
          saveNewFilterValue={saveNewFilterValue as SaveNewFilterValue}
          removeFilter={removeFilter}
          onClose={onClose}
        />
      );
  }
};

export default FilterItemMenuWithQueries;
